

import React, { useEffect, useState } from "react";
import Fuse from "fuse.js";
import { getSettingsData, saveSettingsData } from "src/utils/GetSettingsData";
import {
  DataGridPro,
  LicenseInfo,
  GridColumns,
  GridEnrichedColDef,
  GridNativeColTypes,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridFilterModel,
  GridFilterItem,
  GridLinkOperator,
  GridFooter,
  GridFooterContainer,
  MuiEvent,
  GridSortModel,
  GridColumnResizeParams,
  GridRenderCellParams,
  GridRowParams,
  GridOverlay,
  useGridApiRef,
  GridCallbackDetails,
  GridFilterOperator,
  GridEventListener
} from '@mui/x-data-grid-pro';
import {
  Typography,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  Clear,
  RotateLeft,
  BackupTableTwoTone,
  Search,
  SearchOff
} from "@mui/icons-material";
import SaveIcon from '@mui/icons-material/Save';
import { Theme } from '@mui/material/styles'
import { createStyles, makeStyles } from '@mui/styles';
import { useLocalStorage } from "src/utils/LocalStorageHook";


LicenseInfo.setLicenseKey("55a5960678d2f17e42032f031b42392aTz0xMjY1OTgsRT0xODA4MDQ5NjAwMDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMS0yMDI2LFE9MSxBVD1tdWx0aSxLVj0y");
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: "2px 4px",
      margin: "0 auto",
      display: "flex",
      alignItems: "stretch",
      height: "100%",
      flexDirection: "column",
      position: "relative"
    },
    titleBar: {
      margin: theme.spacing(0),
      padding: theme.spacing(0),
      textAlign: "center",
      width: "100%",
      fontWeight: "bolder",
      backgroundColor: theme.palette.action.selected,
      borderColor: theme.palette.primary.main,
      borderStyle: "solid",
      borderWidth: "1px"
    },
    dgProTable: {
    }
  }));


export interface TableAbstractionOptions {
  draggableColumns?: { enabled?: boolean, },  // ToDo
  responsive?: "vertical" | "standard", //ToDo
  rowsPerPageOptions?: number[],
  paging?: "disabled",
  serverSide?: boolean,  // ToDo
  selectableRows?: "none" | "multiple",  // ToDo
  tableBodyHeight?: string,
  tableHeightReducePx?: number,
  rowsPerPage?: number,
  onChangeRowsPerPage?: ((numberOfRows: number) => void),
  onRowClick?: ((params: GridRowParams) => void),
  onRowEnter?: ((params: GridRowParams) => void),
  sortOrder?: { name: string, direction: "asc" | "desc" },  // ToDo
  customToolbar?: (() => React.ReactNode),
  download?: boolean,
  elevation?: number,  // ToDo
  onRowSelectionChange?: ((currentRowsSelected: any[], allRowsSelected: any[], rowsSelected?: any[] | undefined) => void),

  disableFuzySearch?: boolean,

  customTableBodyFooterRender?: ((opts: any) => void),
  fixedHeader?: boolean,
  print?: boolean,
}

export interface TableAbstractionColumn {
  label?: string,
  name: string,
  disableReorder?: boolean,
  hide?: boolean,
  valueGetter?: ((options: any) => any),
  hideable?: boolean,
  headerClassName?: string,
  options?: {
    type?: GridNativeColTypes,
    display?: boolean,
    searchable?: boolean,
    filter?: boolean,
    renderCell?: ((params: GridRenderCellParams) => React.ReactNode),
    customBodyRender?: ((value: any, tableMeta: any, updateValue: (value: string) => void) => React.ReactNode),
    setCellProps?: ((cellValue?: string, rowIndex?: number, columnIndex?: number) => object),  // ToDo
    customTableBodyFooterRender?: ((options: any) => any), // ToDo
    sort?: boolean, // ToDo
    hint?: any,
    filterType?: string, // ToDo
    filterOptions?: any, // ToDo
    filterOperators?: GridFilterOperator[]
  }
}

export interface TableAbstractionInputs {
  settingsKey: string;
  title?: string;
  options?: TableAbstractionOptions;
  data?: any[];
  columns: TableAbstractionColumn[];
  error?: boolean;
  loading?: boolean;
  handleSaveProps?: any;
  vpRMfiltersData?: boolean;
  downloadFileName?: any
}

interface OSearch {
  enabled: boolean, // does one or more column allow this type of search? 
  searching?: boolean, // is the user searching? 
  text?: string // what is the user searching for?
}

function arrayMove(arr: any[], old_index: number, new_index: number) {
  if (new_index >= arr.length) {
    var k = new_index - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  return arr;
}


export default function TelemeterAbstractionTable(props: TableAbstractionInputs) {
  const classes = useStyles();
  const gridApiRef = useGridApiRef();

  const getIsLoading = () => {
    if (props.loading) {
      return true;
    } else if (!props.data) {
      return true;
    } else {
      return false;
    }
  }

  //set default filter value for status


  //set defaults if not provided
  const rppOptions: number[] = (props.options?.rowsPerPageOptions) || [25, 50, 100]
  const isLoading = getIsLoading();
  const rowData = (props.data || []).map((d: any, id: number) => {
    if (d.id) return d
    else return {
      ...d,
      id
    }
  });
  const key = (props.settingsKey) || 'GTA';
  const hasFilters: boolean = props.columns.filter(c => c.options?.filter).length > 0;
  const pagingDisabled = (props.options?.paging === "disabled")

  const [settingsTableData, setSettingsTableData] = React.useState<any>();

  //local storage
  const [columnSizeLS, setColumnSizeLS] = useLocalStorage<{ [key: string]: number; }>(`SETTINGS.${key}_colsize`, {});
  const [displayLS, setDisplayLS] = useLocalStorage<{ [key: string]: boolean }>(`SETTINGS.${key}_displayableColumns`, {});
  const [filterLS, setFilterLS] = useLocalStorage<GridFilterModel>(`SETTINGS.${key}_filters`, { items: [], linkOperator: GridLinkOperator.And });
  const [sortLS, setSortLS] = useLocalStorage<GridSortModel>(`SETTINGS.${key}_sort`, []);
  const [columnOrder, setColumnOrder] = useLocalStorage<string[]>(`SETTINGS.${key}_column_order`, []);
  const [salesOrg] = useLocalStorage("SETTINGS.salesOrg", '');

  //state variables
  const [page, setPage] = useState<number>(0);
  const [pageRowCount, setPageRowCount] = useState<number>(props.options?.rowsPerPage || rppOptions[0]);
  const [omniSearch, setOmniSearch] = useState<OSearch>({ enabled: false });
  const [isLoaded, setIsLoaded] = useState(false);
  const [confirmResetColumn, setConfirmResetColumn] = React.useState<boolean>(false);
  const [confirmResetFilter, setConfirmResetFilter] = React.useState<boolean>(false);

  const reorderingRef = React.useRef({
    isDragging: false,
    oldIndex: 0,
    targetIndex: 0
  });


  useEffect(() => {
    const handleColumnHeaderDragEnd: GridEventListener<"columnHeaderDragEnd"> = (
      params
    ) => {
      setColumnOrder(arrayMove(columnOrder, reorderingRef.current.oldIndex, reorderingRef.current.targetIndex));
      saveTxSettingsData(JSON.stringify(columnOrder), '_column_order');
      reorderingRef.current = {
        isDragging: false,
        oldIndex: 0,
        targetIndex: 0
      };
    };

    // The `subscribeEvent` method will automatically unsubscribe in the cleanup function of the `useEffect`.
    return gridApiRef.current.subscribeEvent(
      "columnHeaderDragEnd",
      handleColumnHeaderDragEnd
    );
  }, [gridApiRef]);






  useEffect(() => {

    if (salesOrg) {
      getSettingsData(salesOrg).then((v) => {


        for (let i = 0; i < v?.data?.length; i++) {
          if (v?.data[i]?.colKey === `${key}_displayableColumns`) {
            setDisplayLS(JSON.parse(v?.data[i]?.colVal));
          }

          // For filters

          if (v?.data[i]?.colKey === `${key}_filters`) {
            setFilterLS(JSON.parse(v?.data[i]?.colVal));
          }

          // For coloumn order

          if (v?.data[i]?.colKey === `${key}_column_order`) {
            setColumnOrder(JSON.parse(v?.data[i]?.colVal));
          }

          // For column size

          if (v?.data[i]?.colKey === `${key}_colsize`) {
            setColumnSizeLS(JSON.parse(v?.data[i]?.colVal));
          }

          // For column sort

          if (v?.data[i]?.colKey === `${key}_sort`) {
            setSortLS(JSON.parse(v?.data[i]?.colVal));
          }

        }
    
      });

    }
  }, []);

  useEffect(() => {
    if (isLoaded) return;
    if (props.options && props.options.sortOrder) {
      setSortLS([{ field: props.options?.sortOrder?.name, sort: props.options?.sortOrder?.direction }]);
      setIsLoaded(true);
    }


  }, [isLoaded, props.options]);

  function defaultColumnConfig(field: string, display: boolean, width?: number) {
    let ret: any = {
      disableColumnMenu: true
    };
    if (field) {
      ret = {
        ...ret,
        field,
        hide: (!display) ? true : (displayLS[field] !== undefined && !displayLS[field]),
        width: columnSizeLS[field] || width || 150
      };
    }
    return ret;
  }

  function getColumns(columns: TableAbstractionColumn[]) {
    const ret: GridColumns = columns.map(c => {
      let display = true
      if (c.options?.display !== undefined) { display = c.options.display }
      if (c.options?.searchable && !(omniSearch.enabled)) {
        setOmniSearch({ enabled: true });
      }
      const r: GridEnrichedColDef = {
        ...defaultColumnConfig(c.name, display),
        headerClassName: c.headerClassName,
        headerName: c.label || c.name,
        hideable: c.hideable,
        valueGetter: c.valueGetter,
        type: (c.options?.type) || 'string',
        filterable: c.options?.filter,
        searchable: c.options?.searchable
      }
      if (c.options?.filterOperators) r.filterOperators = c.options?.filterOperators;

      if (c.options && c.options.renderCell && !!(c.options.renderCell)) {
        r.renderCell = c.options.renderCell;
      } else if (c.options && c.options.customBodyRender && !!(c.options.customBodyRender)) {
        const renderMethod = c.options.customBodyRender;
        r.renderCell = (params: any) => renderMethod(params.value, undefined, () => { })
      }

      if (c.options?.hint !== undefined) {
        r.description = c.options.hint
        r.headerName += ` ❓`
      }
      return r;
    })
    return ret;
  }


  function getSortedColumns(columns: TableAbstractionColumn[]) {
    const cols = getColumns(columns);
    let colSrt = columnOrder;
    const colNamesAll = cols.map((c) => c.field);
    const colNamesNew = colNamesAll?.filter((cn) => {
      return -1 === columnOrder?.findIndex((co) => co === cn);
    });
    if (colNamesNew.length > 0) {
      colSrt = colSrt?.concat(colNamesNew);
      setColumnOrder(colSrt);
    }

    const getSortIndex = (field: string) => {
      return colSrt?.findIndex((co) => co === field);
    };
    const sortFunction = (a: GridEnrichedColDef, b: GridEnrichedColDef) => {
      return getSortIndex(a.field) - getSortIndex(b.field);
    };
    return cols.sort(sortFunction);
  }

  if (pagingDisabled) {

  }
  function CustomToolbar(title?: string, save?: any) {
    let timeout: NodeJS.Timeout;
    let text = '';

    function enableSearch(searching: boolean) {
      setOmniSearch({ ...omniSearch, searching, text: undefined })
    }
    function setSearch(ce: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
      text = ce.target.value || ''
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setOmniSearch({ ...omniSearch, text })
      }, 3000)
    }
    const handleSave = () => {
      save.handleSave()
    }

    return (


      <React.Fragment>
        <GridToolbarContainer key={key + "toolbar"}>
          {(omniSearch.enabled) ? (
            (omniSearch.searching) ? (
              <div
                key={key + "omniSearchDiv"}
                style={{ verticalAlign: "middle" }}
              >
                <TextField
                  key={key + "omniSearchTextField"}
                  fullWidth={false}
                  size="small"
                  variant="outlined"
                  defaultValue={omniSearch.text}
                  onChange={setSearch}
                  onKeyPress={(kp) => {
                    if (kp.code === 'Enter') {
                      clearTimeout(timeout);
                      setOmniSearch({ ...omniSearch, text })
                    }
                  }}
                />
                <Button
                  key={key + "omniSearchButton"}
                  color="primary"
                  size="small"
                  onClick={() => enableSearch(false)}
                >
                  <SearchOff />
                </Button>
              </div>
            ) : (
              <Button
                color="primary"
                size="small"
                onClick={() => enableSearch(true)}
              >
                <Search /> Search
              </Button>
            )
          ) : (<div />)}
          <GridToolbarColumnsButton nonce={undefined} onResize={undefined} onResizeCapture={undefined} />
          <Button
            color="primary"
            size="small"
            onClick={() => {
              setConfirmResetColumn(true);
            }}
          >
            <RotateLeft /> Reset Columns
          </Button>
          {(hasFilters) ? (
            <div>
              <GridToolbarFilterButton nonce={undefined} onResize={undefined} onResizeCapture={undefined} />
              <Button
                color="primary"
                size="small"
                onClick={() => {
                  setConfirmResetFilter(true)
                }
                }
              >
                <Clear /> Clear Filters
              </Button>
            </div>
          ) : (<div />)}

          <GridToolbarDensitySelector nonce={undefined} onResize={undefined} onResizeCapture={undefined} />
          {(props.options?.download !== undefined && props.options?.download === false) ? <div /> : (<GridToolbarExport csvOptions={{
            fileName: props.downloadFileName || 'CONA_NextGen_Vending',
          }} />)}
          {(props.options?.customToolbar) && props.options?.customToolbar()}
          {props?.handleSaveProps?.visible &&
            (
              <Button
                color="primary"
                size="small"
                onClick={handleSave}
                startIcon={<SaveIcon />}
              >
                Save
              </Button>
            )}

        </GridToolbarContainer>

        <Dialog
          open={confirmResetColumn}
          onClose={() => setConfirmResetColumn(false)}
        >
          <DialogTitle>Confirm Reset Columns</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Column sequence, column sort, column width and displayable columns will be reset to default values.Are you sure you wish to continue ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { handleResetColumns(); setConfirmResetColumn(false); }} color="primary">
              Yes
            </Button>
            <Button onClick={() => { setConfirmResetColumn(false); }} color="primary" autoFocus>
              No
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmResetFilter}
          onClose={() => setConfirmResetFilter(false)}
        >
          <DialogTitle>Confirm Reset Filters</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Column filters will be reset. Are you sure you wish to continue ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { handleResetFilters(); setConfirmResetFilter(false) }} color="primary">
              Yes
            </Button>
            <Button onClick={() => { setConfirmResetFilter(false); }} color="primary" autoFocus>
              No
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>

    );
  }

  function CustomFooter(opts: any) {

    if ((props.options?.customTableBodyFooterRender) === undefined) {
      return (pagingDisabled) ? <div /> : <GridFooter />
    }

    return (
      <GridFooterContainer>
        {props.options?.customTableBodyFooterRender(opts)}
        {(pagingDisabled) ? <div /> : <GridFooter />}
      </GridFooterContainer>
    );
  }


  const handleResetColumns = () => {
    setColumnSizeLS({});
    setDisplayLS({});
    setColumnOrder([]);
    saveTxSettingsData(JSON.stringify({}), '_colsize');
    saveTxSettingsData(JSON.stringify({}), '_displayableColumns');
    saveTxSettingsData(JSON.stringify([]), '_column_order');
  }

  const handleResetFilters = () => {
    setFilterLS({ items: [], linkOperator: GridLinkOperator.And })
    saveTxSettingsData(JSON.stringify({ items: [], linkOperator: GridLinkOperator.And }), '_filters');

  }


  function handleFilterModelChange(params: GridFilterModel) {
    setTimeout(function () {
      setFilterLS(params);

    }, 1000);


  }
  function handleColumnModelChange(params: any) {
    if (JSON.stringify(params) !== JSON.stringify(displayLS)){
      setDisplayLS(params);
      saveTxSettingsData(JSON.stringify(params), '_displayableColumns');
    }
  }
  function handleSortModelChange(params: GridSortModel) {
    if (JSON.stringify(params) !== JSON.stringify(sortLS))
      setSortLS(params);
    saveTxSettingsData(JSON.stringify(params), '_sort');
  }

  function handleColumnOrderChange(params: any) {
    setColumnOrder(arrayMove(columnOrder, params.oldIndex, params.targetIndex));
    saveTxSettingsData(JSON.stringify(columnOrder), '_column_order');
  }

  function handleColumnResize(params: GridColumnResizeParams) {
    columnSizeLS[params.colDef.field] = params.width;
    setColumnSizeLS(columnSizeLS);
    saveTxSettingsData(JSON.stringify(columnSizeLS), '_colsize');
  }


  function saveTxSettingsData(value: string, columnName: string) {
    const colKey = (key + columnName);
    if (salesOrg) {
      saveSettingsData(salesOrg, colKey, value).then((v) => {
      
      });
    }
  }


  const divMainStyle: React.CSSProperties = {}

  if (props.options?.tableBodyHeight && props.options?.tableBodyHeight !== '') {
    divMainStyle.height = props.options.tableBodyHeight;
  } else if (props.options?.responsive && props.options?.responsive === 'vertical') {

  } else {
    divMainStyle.height = (window.innerHeight - 135 - (props.options?.tableHeightReducePx || 0))
  }

  const fuzzySearch = (props.options?.disableFuzySearch);
  const fuseOptions = {
    isCaseSensitive: false,
    //minMatchCharLength : (omniSearch.text || '').length / 2,
    threshold: fuzzySearch ? 0.2 : 0,
    keys: (props.columns || []).filter(c => (c.options?.searchable) || false).map(c => c.name)
  };

  const fuse = new Fuse(rowData, fuseOptions);



  function getFilteredRowData() {
    const enabled = omniSearch.enabled || false;
    const searching = omniSearch.searching || false;
    const text = omniSearch.text || '';
    if (!enabled || !searching || text === '' || fuseOptions.keys.length === 0) {
      return rowData
    }
    return fuse.search(text).map(f => f.item) || []
  }
  const filteredRowData = getFilteredRowData();

  return (
    <div
      key={key + "main-div"}
      className={classes.root}
      style={divMainStyle}
    >

      {props.title &&
        <Typography
          className={classes.titleBar}
          variant="h5"
          color="primary"
        >
          {props.title}
        </Typography>}
      {
   
      }

      <DataGridPro
        className={classes.dgProTable}
        key={key}
        apiRef={gridApiRef}
        error={props.error}
        loading={isLoading}
        filterModel={filterLS}
        sortModel={sortLS}
        components={{
          Toolbar: () => CustomToolbar(props.title, props?.handleSaveProps),
          Footer: CustomFooter
          //NoRowsOverlay: CustomNoRowsOverlay,
        }}
        columnBuffer={10}

        density={"compact"}
        autoHeight={props.options?.responsive === 'vertical'}


        onRowClick={props.options?.onRowClick}
        //onRowEnter={handleRowEnter}
        onFilterModelChange={handleFilterModelChange}
        onSortModelChange={handleSortModelChange}
        onColumnVisibilityModelChange={handleColumnModelChange}
        //onColumnOrderChange={handleColumnOrderChange}
        onColumnWidthChange={handleColumnResize}
        onColumnOrderChange={(params) => {
          reorderingRef.current = reorderingRef.current.isDragging
            ? { ...reorderingRef.current, targetIndex: params.targetIndex }
            : {
              isDragging: true,
              oldIndex: params.oldIndex,
              targetIndex: params.targetIndex
            };
        }}
        rowsPerPageOptions={rppOptions}
        onPageSizeChange={(params: any) => { setPageRowCount(params); }}
        pagination
        page={page}
        onPageChange={(params: any) => { setPage(params); localStorage.setItem("SETTINGS.CLP_PageNumber", "0"); }}
        pageSize={pageRowCount}
        //getRowId={(row) => row.customerNum}
        rows={filteredRowData}

        columns={getSortedColumns(props.columns || [])}

      />
    </div>
  );
}






